import { FC, useMemo, useState } from "react";
import { Bank } from "../@types";
import { CaretDown, CaretUp, X } from "@phosphor-icons/react";
import { getBankLogo } from "../helpers/logos";

const BankListInput: FC<{
  bankListData?: Bank[];
  isLoading?: boolean;
  onSelect: (bank: Bank) => void;
  selected?: string;
}> = ({ onSelect, selected, isLoading, bankListData }) => {
  const [search, setSearch] = useState("");

  const [isOpen, setIsOpen] = useState(false);

  const s = useMemo(() => {
    if (!bankListData || !bankListData) {
      return null;
    }

    return bankListData.find((bank) => bank.code === selected);
  }, [selected, bankListData]);

  const filteredBanks = useMemo(() => {
    if (!bankListData) return [];
    const q = search.toLowerCase();
    return bankListData
      .filter(
        (bank) =>
          (bank.name ?? "").toLowerCase().includes(q) ||
          (bank.code ?? "").toLowerCase().includes(q)
      )
      .sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {
          sensitivity: "base",
        })
      );
  }, [bankListData, search]);

  return (
    <div
      className={`relative rounded-lg z-10 text-left border border-gray-300 shadow w-full cursor-pointer text-gray-800 ${
        isLoading && "opacity-50 cursor-not-allowed bg-gray-100"
      }`}
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {s ? (
          <div className="m-0 flex gap-2 items-center cursor-pointer">
            <div className="w-8 h-8 rounded-full">
              <img
                src={getBankLogo(s)}
                alt={s?.name}
                className="w-full h-full object-contain rounded-md"
              />
            </div>
            <div className="text-gray-900">{s.name}</div>
          </div>
        ) : (
          <span className="text-gray-700">
            {isLoading ? "Loading" : "Select bank"}
          </span>
        )}
        {isOpen ? <CaretDown /> : <CaretUp />}
      </div>
      {isOpen && bankListData && bankListData && (
        <div className="absolute top-full mt-2 max-h-52 overflow-scroll z-[999999999999999] bg-white w-full left-0 rounded-lg border border-gray-300">
          <div className="flex items-center justify-between px-4 py-2 border-gray-300 border-b">
            <input
              disabled={isLoading}
              onClick={(e) => e.stopPropagation()}
              type="text"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Bank"
              className=" flex-1 outline-none focus:outline-none"
            />
            <div
              onClick={() => {
                setIsOpen(false);
              }}
              className="p-2 bg-gray-100 rounded-full"
            >
              <X weight="bold" size={16} />
            </div>
          </div>
          {filteredBanks.map((option, i) => {
              return (
                <div
                  key={i}
                  onClick={(e) => {
                    onSelect({ ...option, logo: getBankLogo(option) });
                    setSearch("");
                    setIsOpen(false);
                    e.stopPropagation();
                  }}
                  className="hover:bg-gray-200 py-4 m-0 px-2 flex gap-3 items-center cursor-pointer text-gray-900"
                >
                  <div className="w-6 h-6 rounded-full">
                    <img
                      src={getBankLogo(option)}
                      alt={option.name}
                      className="w-full h-full object-contain rounded-md"
                    />
                  </div>
                  <div className="line-clamp-1 text-ellipsis text-gray-900">
                    {option.name}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default BankListInput;
